export default function initTabBars(): void {
    const tabBarContainers = document.querySelectorAll<HTMLElement>('.js-tab-bar-container');

    tabBarContainers.forEach(container => {
        new TabBarHandler(container);
    });
}

/**
 * Gestion d'un système d'onglets accessible (ARIA Tabs pattern)
 *
 * Markup attendu :
 *   <div class="js-tab-bar-container">
 *     <div class="js-tab-bar-tabs" role="tablist">
 *       <button role="tab" id="x-tab-1" aria-controls="x-panel-1" aria-selected="true"  tabindex="0">…</button>
 *       <button role="tab" id="x-tab-2" aria-controls="x-panel-2" aria-selected="false" tabindex="-1">…</button>
 *     </div>
 *     <div role="tabpanel" id="x-panel-1" aria-labelledby="x-tab-1">…</div>
 *     <div role="tabpanel" id="x-panel-2" aria-labelledby="x-tab-2" hidden>…</div>
 *   </div>
 *
 * Navigation clavier :
 *   ArrowRight / ArrowLeft → onglet suivant / précédent (focus + activation)
 *   Home                   → premier onglet
 *   End                    → dernier onglet
 */
export class TabBarHandler {
    private container: HTMLElement;
    private tabs: HTMLElement[];
    private panels: HTMLElement[];
    private activeIndex = 0;

    constructor(container: HTMLElement) {
        this.container = container;
        this.tabs   = Array.from(container.querySelectorAll<HTMLElement>('[role="tab"]'));
        this.panels = Array.from(container.querySelectorAll<HTMLElement>('[role="tabpanel"]'));

        if (this.tabs.length && this.panels.length) {
            this.init();
        }
    }

    private init(): void {
        // Déterminer l'onglet initialement actif depuis le DOM
        const initialActive = this.tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
        this.activeIndex = initialActive >= 0 ? initialActive : 0;
        this.activate(this.activeIndex, false);

        this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.activate(index));
            tab.addEventListener('keydown', (e) => this.onKeyDown(e, index));
        });
    }

    activate(index: number, moveFocus = true): void {
        this.tabs.forEach((tab, i) => {
            const isActive = i === index;
            tab.setAttribute('aria-selected', String(isActive));
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
            tab.classList.toggle('js-tab-bar-tab--active', isActive);
        });

        this.panels.forEach((panel, i) => {
            const isActive = i === index;
            panel.classList.toggle('js-tab-bar-content--active', isActive);
            if (isActive) {
                panel.removeAttribute('hidden');
            } else {
                panel.setAttribute('hidden', '');
            }
        });

        this.activeIndex = index;

        if (moveFocus) {
            this.tabs[index]?.focus();
        }
    }

    private onKeyDown(e: KeyboardEvent, currentIndex: number): void {
        const last = this.tabs.length - 1;
        let target = -1;

        switch (e.key) {
            case 'ArrowRight': target = currentIndex < last ? currentIndex + 1 : 0;    break;
            case 'ArrowLeft':  target = currentIndex > 0    ? currentIndex - 1 : last; break;
            case 'Home':       target = 0;                                              break;
            case 'End':        target = last;                                           break;
            default: return;
        }

        e.preventDefault();
        this.activate(target);
    }
}
